import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { db } from "../firebaseConfig";
// ContractorsPage.css is also imported by LicenseCertificates.js and
// CompletedJobs.js — keep importing it unchanged. New styling is scoped
// under .opsui- (see OpsUi.css).
import "./ContractorsPage.css";
import {
  OpsModal,
  ConfirmDialog,
  Pill,
  StatusPill,
  DuePill,
  Field,
  EmptyState,
  StateSelect,
  SortHeader,
  ReminderRecipientsCard,
  dueStatus,
  formatDate,
} from "./OpsUi";

const EMPTY_CONTRACTOR = {
  staffId: "",
  linkedToStaff: false,
  companyName: "",
  firstName: "",
  lastName: "",
  contactName: "",
  email: "",
  phone: "",
  ein: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  skills: [],
  otherSkills: "",
  industry: "",
  status: "Active",
  insuranceCertificates: "No",
  generalLiability: "",
  generalLiabilityPolicy: "",
  commercialAuto: "",
  commercialAutoPolicy: "",
  workersComp: "",
  workersCompPolicy: "",
  contractorLicenseNumber: "",
  contractorLicenseType: "",
  contractorLicenseState: "",
  contractorLicenseExpiration: "",
  notes: "",
  complianceFiles: [],
};

// Every date the compliance status / reminder emails look at.
const COMPLIANCE_DATES = [
  ["generalLiability", "General Liability"],
  ["commercialAuto", "Commercial Auto"],
  ["workersComp", "Workers Comp"],
  ["contractorLicenseExpiration", "License"],
];

const complianceSummary = (record) => {
  const list = COMPLIANCE_DATES.map(([key, policy]) => ({ key, policy, value: record[key], ...dueStatus(record[key]) }))
    .filter((s) => s.tone !== "neutral")
    .sort((a, b) => b.rank - a.rank || a.days - b.days);
  return list[0] || null;
};

const displayName = (c) => c.companyName || c.contactName || "Unnamed Contractor";

const ContractorsPage = () => {
  const [contractors, setContractors] = useState([]);
  const [subContractorStaff, setSubContractorStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newContractor, setNewContractor] = useState({ ...EMPTY_CONTRACTOR });
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [editedContractor, setEditedContractor] = useState(null);
  const [showAddContractorPopup, setShowAddContractorPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [editFiles, setEditFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null); // { file, index }

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [complianceFilter, setComplianceFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "name", dir: "asc" });

  const storage = getStorage();

  const fetchData = async () => {
    const [contractorSnap, usersSnap] = await Promise.all([
      getDocs(collection(db, "contractors")),
      getDocs(collection(db, "users")),
    ]);
    setContractors(contractorSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setSubContractorStaff(
      usersSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => item.workerType === "Sub Contractor")
    );
  };

  useEffect(() => {
    fetchData()
      .catch((error) => console.error("Error loading contractors:", error))
      .finally(() => setLoading(false));
  }, []);

  const formatSkills = (record) =>
    [
      ...(Array.isArray(record.skills) ? record.skills : []),
      ...(record.otherSkills
        ? String(record.otherSkills).split(",").map((item) => item.trim()).filter(Boolean)
        : []),
    ].join(", ") || "None";

  const uploadFiles = async (contractorId, files) => {
    const uploaded = [];
    for (const file of Array.from(files || [])) {
      const safeName = `${Date.now()}-${file.name}`.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileRef = ref(storage, `contractors/${contractorId}/${safeName}`);
      await uploadBytes(fileRef, file);
      uploaded.push({
        name: file.name,
        url: await getDownloadURL(fileRef),
        type: file.type || "",
        size: file.size || 0,
        uploadedAt: new Date().toISOString(),
      });
    }
    return uploaded;
  };

  const handleLinkedStaffChange = (staffId) => {
    if (!staffId) {
      setNewContractor((prev) => ({ ...prev, staffId: "", linkedToStaff: false }));
      return;
    }
    const staff = subContractorStaff.find((item) => item.id === staffId);
    if (!staff) return;
    setNewContractor((prev) => ({
      ...prev,
      staffId: staff.id,
      linkedToStaff: true,
      firstName: staff.firstName || "",
      lastName: staff.lastName || "",
      contactName: `${staff.firstName || ""} ${staff.lastName || ""}`.trim(),
      email: staff.email || "",
      phone: staff.phone || "",
      ein: staff.ein || "",
      address: staff.address || "",
      city: staff.city || "",
      state: staff.state || "",
      zipCode: staff.zip || "",
      skills: Array.isArray(staff.skills) ? staff.skills : [],
      otherSkills: staff.otherSkills || "",
      status: staff.isActive === false ? "Inactive" : "Active",
    }));
  };

  const onNewChange = (e) => {
    const { name, value } = e.target;
    setNewContractor((prev) => ({ ...prev, [name]: value }));
  };

  const onEditChange = (e) => {
    const { name, value } = e.target;
    setEditedContractor((prev) => ({ ...prev, [name]: value }));
  };

  // A changed expiration date should be able to remind again, so clear the
  // reminder log for just that field (same idea as Vehicles).
  const resetChangedReminderLog = (before = {}, after = {}) => {
    const log = { ...(before.notificationLog || {}) };
    let changed = false;
    COMPLIANCE_DATES.forEach(([key]) => {
      if ((before[key] || "") !== (after[key] || "") && log[key]) {
        delete log[key];
        changed = true;
      }
    });
    return changed ? { notificationLog: log } : {};
  };

  const closeAdd = () => {
    setShowAddContractorPopup(false);
    setNewContractor({ ...EMPTY_CONTRACTOR });
    setNewFiles([]);
  };

  const saveNewContractor = async (e) => {
    e?.preventDefault?.();
    if (!String(newContractor.companyName || "").trim() && !String(newContractor.contactName || "").trim()) {
      alert("Please enter a Company Name or Contact Name.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...newContractor,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, "contractors"), payload);
      const uploaded = await uploadFiles(docRef.id, newFiles);
      if (uploaded.length) {
        payload.complianceFiles = uploaded;
        await updateDoc(doc(db, "contractors", docRef.id), { complianceFiles: uploaded });
      }
      setContractors((prev) => [...prev, { id: docRef.id, ...payload }]);
      closeAdd();
    } catch (error) {
      console.error("Error adding contractor:", error);
      alert("Could not add contractor.");
    } finally {
      setSaving(false);
    }
  };

  const saveEditedContractor = async (e) => {
    e?.preventDefault?.();
    if (!editedContractor?.id) return;
    setSaving(true);
    try {
      const uploaded = await uploadFiles(editedContractor.id, editFiles);
      const payload = {
        ...editedContractor,
        ...resetChangedReminderLog(selectedContractor, editedContractor),
        complianceFiles: [
          ...(Array.isArray(editedContractor.complianceFiles) ? editedContractor.complianceFiles : []),
          ...uploaded,
        ],
        updatedAt: new Date().toISOString(),
      };
      const updatePayload = { ...payload };
      delete updatePayload.id;
      await updateDoc(doc(db, "contractors", editedContractor.id), updatePayload);

      setContractors((prev) => prev.map((item) => (item.id === editedContractor.id ? payload : item)));
      setSelectedContractor(payload);
      setEditedContractor(payload);
      setEditFiles([]);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving contractor:", error);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComplianceFile = async () => {
    if (!selectedContractor?.id || !fileToDelete) return;
    const { file, index: fileIndex } = fileToDelete;
    setDeleting(true);
    try {
      const currentFiles = Array.isArray(selectedContractor.complianceFiles)
        ? selectedContractor.complianceFiles
        : [];
      if (file?.url) {
        try {
          await deleteObject(ref(storage, file.url));
        } catch (storageError) {
          if (storageError?.code !== "storage/object-not-found") throw storageError;
        }
      }
      const nextFiles = currentFiles.filter((_, index) => index !== fileIndex);
      await updateDoc(doc(db, "contractors", selectedContractor.id), {
        complianceFiles: nextFiles,
        updatedAt: new Date().toISOString(),
      });
      setContractors((prev) =>
        prev.map((item) => (item.id === selectedContractor.id ? { ...item, complianceFiles: nextFiles } : item))
      );
      setSelectedContractor((prev) => ({ ...prev, complianceFiles: nextFiles }));
      setEditedContractor((prev) => (prev ? { ...prev, complianceFiles: nextFiles } : prev));
      setFileToDelete(null);
    } catch (error) {
      console.error("Error deleting contractor file:", error);
      alert("Unable to delete this file.");
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedContractor) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "contractors", selectedContractor.id));
      setContractors((prev) => prev.filter((c) => c.id !== selectedContractor.id));
      setShowDeleteConfirmation(false);
      setSelectedContractor(null);
      setEditedContractor(null);
    } catch (error) {
      console.error("Error deleting contractor:", error);
      alert("Could not delete contractor.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (key) =>
    setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedContractors = useMemo(() => {
    const direction = sortConfig.dir === "asc" ? 1 : -1;
    const q = search.trim().toLowerCase();
    const getValue = (item) => {
      if (sortConfig.key === "skills") return formatSkills(item);
      if (sortConfig.key === "status") return item.status || "";
      if (sortConfig.key === "insurance") return item.insuranceCertificates || "";
      if (sortConfig.key === "license") return item.contractorLicenseExpiration || "9999";
      if (sortConfig.key === "compliance") {
        const s = complianceSummary(item);
        return s ? String(10 - s.rank) + String(100000 + (s.days || 0)).padStart(7, "0") : "99";
      }
      return item.companyName || item.contactName || "";
    };
    return contractors
      .filter((c) => {
        const status = c.status || "Active";
        if (statusFilter === "active" && status !== "Active") return false;
        if (statusFilter === "inactive" && status === "Active") return false;
        if (complianceFilter !== "all") {
          const s = complianceSummary(c);
          if (complianceFilter === "attention" && !(s && (s.tone === "warn" || s.tone === "danger"))) return false;
          if (complianceFilter === "expired" && !(s && s.tone === "danger")) return false;
        }
        if (!q) return true;
        return [c.companyName, c.contactName, c.email, c.phone, c.industry, formatSkills(c), c.contractorLicenseNumber]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      })
      .sort(
        (a, b) =>
          String(getValue(a)).localeCompare(String(getValue(b)), undefined, {
            numeric: true,
            sensitivity: "base",
          }) * direction
      );
  }, [contractors, sortConfig, search, statusFilter, complianceFilter]);

  const stats = useMemo(() => {
    const active = contractors.filter((c) => (c.status || "Active") === "Active");
    let soon = 0;
    let expired = 0;
    active.forEach((c) => {
      const s = complianceSummary(c);
      if (s?.tone === "danger") expired += 1;
      else if (s?.tone === "warn") soon += 1;
    });
    return { total: contractors.length, active: active.length, soon, expired };
  }, [contractors]);

  const openDetails = (contractor) => {
    setSelectedContractor(contractor);
    setEditedContractor({ ...contractor });
    setEditFiles([]);
    setIsEditing(false);
  };

  const closeDetails = () => {
    setSelectedContractor(null);
    setEditedContractor(null);
    setIsEditing(false);
  };

  // ------------------------------------------------------------------ form
  const renderForm = (record, onChange, isNew) => {
    const linked = Boolean(record.linkedToStaff && record.staffId);
    const pendingFiles = isNew ? newFiles : editFiles;

    return (
      <div className="opsui-form-grid">
        {isNew && (
          <Field
            label="Link existing staff sub contractor"
            wide
            hint="Only Staff records marked “Sub Contractor” appear here. Linked contact details and skills are maintained on the Staff page."
          >
            <select
              className="opsui-select"
              value={record.staffId || ""}
              onChange={(e) => handleLinkedStaffChange(e.target.value)}
            >
              <option value="">Not linked / independent contractor</option>
              {subContractorStaff.map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {`${staff.firstName || ""} ${staff.lastName || ""}`.trim() || staff.email}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="opsui-section-title">Company & contact</div>
        <Field label="Company Name">
          <input className="opsui-input" name="companyName" value={record.companyName || ""} onChange={onChange} />
        </Field>
        <Field label="Contact Name">
          <input className="opsui-input" name="contactName" value={record.contactName || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <Field label="Email">
          <input className="opsui-input" name="email" type="email" value={record.email || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <Field label="Phone">
          <input className="opsui-input" name="phone" type="tel" value={record.phone || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <Field label="EIN">
          <input className="opsui-input" name="ein" value={record.ein || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <Field label="Industry / Trade">
          <input className="opsui-input" name="industry" value={record.industry || ""} onChange={onChange} />
        </Field>
        <Field label="Address" wide>
          <input className="opsui-input" name="address" value={record.address || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <Field label="City">
          <input className="opsui-input" name="city" value={record.city || ""} onChange={onChange} readOnly={linked} />
        </Field>
        <div className="opsui-form-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="State">
            {linked ? (
              <input className="opsui-input" name="state" value={record.state || ""} readOnly />
            ) : (
              <StateSelect value={record.state} onChange={onChange} />
            )}
          </Field>
          <Field label="Zip Code">
            <input className="opsui-input" name="zipCode" value={record.zipCode || ""} onChange={onChange} readOnly={linked} />
          </Field>
        </div>
        <Field label="Status">
          <select className="opsui-select" name="status" value={record.status || "Active"} onChange={onChange}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Terminated">Terminated</option>
          </select>
        </Field>
        <Field label="Skills">
          <div className="opsui-input" style={{ display: "flex", alignItems: "center", background: "#f8fafc" }}>
            {formatSkills(record)}
          </div>
        </Field>
        {!linked && (
          <Field label="Other skills" wide hint="Comma separated">
            <input className="opsui-input" name="otherSkills" value={record.otherSkills || ""} onChange={onChange} />
          </Field>
        )}

        <div className="opsui-section-title">Insurance</div>
        <Field label="Insurance Certificates">
          <select
            className="opsui-select"
            name="insuranceCertificates"
            value={record.insuranceCertificates || "No"}
            onChange={onChange}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </Field>
        <div />
        <Field label="General Liability Policy #">
          <input className="opsui-input" name="generalLiabilityPolicy" value={record.generalLiabilityPolicy || ""} onChange={onChange} />
        </Field>
        <Field label="General Liability Expiration">
          <input className="opsui-input" name="generalLiability" type="date" value={record.generalLiability || ""} onChange={onChange} />
        </Field>
        <Field label="Commercial Auto Policy #">
          <input className="opsui-input" name="commercialAutoPolicy" value={record.commercialAutoPolicy || ""} onChange={onChange} />
        </Field>
        <Field label="Commercial Auto Expiration">
          <input className="opsui-input" name="commercialAuto" type="date" value={record.commercialAuto || ""} onChange={onChange} />
        </Field>
        <Field label="Workers Comp Policy #">
          <input className="opsui-input" name="workersCompPolicy" value={record.workersCompPolicy || ""} onChange={onChange} />
        </Field>
        <Field label="Workers Comp Expiration">
          <input className="opsui-input" name="workersComp" type="date" value={record.workersComp || ""} onChange={onChange} />
        </Field>

        <div className="opsui-section-title">License</div>
        <Field label="License Number">
          <input className="opsui-input" name="contractorLicenseNumber" value={record.contractorLicenseNumber || ""} onChange={onChange} />
        </Field>
        <Field label="License Type / Class">
          <input className="opsui-input" name="contractorLicenseType" value={record.contractorLicenseType || ""} onChange={onChange} />
        </Field>
        <Field label="License State">
          <StateSelect name="contractorLicenseState" value={record.contractorLicenseState} onChange={onChange} />
        </Field>
        <Field label="License Expiration">
          <input
            className="opsui-input"
            name="contractorLicenseExpiration"
            type="date"
            value={record.contractorLicenseExpiration || ""}
            onChange={onChange}
          />
        </Field>

        <div className="opsui-section-title">Notes & files</div>
        <Field label="Notes" wide>
          <textarea className="opsui-textarea" name="notes" rows="4" value={record.notes || ""} onChange={onChange} />
        </Field>
        <Field
          label="Insurance / License / Compliance Files"
          wide
          hint={pendingFiles.length ? `${pendingFiles.length} file(s) will upload when you save.` : "COIs, licenses, W-9s…"}
        >
          <input
            className="opsui-file"
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(e) =>
              isNew ? setNewFiles(Array.from(e.target.files || [])) : setEditFiles(Array.from(e.target.files || []))
            }
          />
        </Field>
      </div>
    );
  };

  // ------------------------------------------------------------------ details
  const renderDetails = (c) => {
    const files = Array.isArray(c.complianceFiles) ? c.complianceFiles : [];
    const legacyFiles = Array.isArray(c.fileUpload) ? c.fileUpload : [];
    const address = [c.address, [c.city, c.state].filter(Boolean).join(", "), c.zipCode].filter(Boolean).join(" ");
    return (
      <div style={{ display: "grid", gap: 18 }}>
        <div className="opsui-pills">
          <StatusPill status={c.status || "Active"} />
          {c.staffId && <Pill tone="info">Linked to Staff</Pill>}
          {c.industry && <Pill tone="neutral" plain>{c.industry}</Pill>}
        </div>

        <dl className="opsui-dl">
          <div>
            <dt>Company</dt>
            <dd>{c.companyName || "—"}</dd>
          </div>
          <div>
            <dt>Contact</dt>
            <dd>{c.contactName || "—"}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{c.email ? <a className="opsui-link" href={`mailto:${c.email}`}>{c.email}</a> : "—"}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{c.phone ? <a className="opsui-link" href={`tel:${c.phone}`}>{c.phone}</a> : "—"}</dd>
          </div>
          <div>
            <dt>EIN</dt>
            <dd>{c.ein || "—"}</dd>
          </div>
          <div>
            <dt>Skills</dt>
            <dd>{formatSkills(c)}</dd>
          </div>
          <div className="is-wide">
            <dt>Address</dt>
            <dd>{address || "—"}</dd>
          </div>
        </dl>

        <div className="opsui-card" style={{ margin: 0 }}>
          <div className="opsui-card-head">
            <h3 className="opsui-card-title">Insurance & license</h3>
            <Pill tone={c.insuranceCertificates === "Yes" ? "success" : "neutral"}>
              Certificates on file: {c.insuranceCertificates || "No"}
            </Pill>
          </div>
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <thead>
                <tr>
                  <th>Coverage</th>
                  <th>Policy / Number</th>
                  <th>Expiration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>General Liability</td>
                  <td>{c.generalLiabilityPolicy || "—"}</td>
                  <td><DuePill value={c.generalLiability} /></td>
                </tr>
                <tr>
                  <td>Commercial Auto</td>
                  <td>{c.commercialAutoPolicy || "—"}</td>
                  <td><DuePill value={c.commercialAuto} /></td>
                </tr>
                <tr>
                  <td>Workers Comp</td>
                  <td>{c.workersCompPolicy || "—"}</td>
                  <td><DuePill value={c.workersComp} /></td>
                </tr>
                <tr>
                  <td>
                    License
                    {c.contractorLicenseType && <div className="opsui-cell-sub">{c.contractorLicenseType}</div>}
                  </td>
                  <td>
                    {c.contractorLicenseNumber || "—"}
                    {c.contractorLicenseState && <div className="opsui-cell-sub">{c.contractorLicenseState}</div>}
                  </td>
                  <td><DuePill value={c.contractorLicenseExpiration} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 6 }}>Notes</div>
          <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
            {c.notes || <span className="opsui-muted">No notes.</span>}
          </div>
        </div>

        <div>
          <div className="opsui-label" style={{ marginBottom: 8 }}>Compliance files</div>
          {files.length || legacyFiles.length ? (
            <ul className="opsui-list">
              {files.map((file, index) => (
                <li key={`${file.url || file}-${index}`} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <a className="opsui-link" href={file.url || file} target="_blank" rel="noreferrer">
                      📄 {file.name || `File ${index + 1}`}
                    </a>
                    {file.uploadedAt && <div className="opsui-list-meta">Uploaded {formatDate(file.uploadedAt)}</div>}
                  </div>
                  <button
                    type="button"
                    className="opsui-btn is-danger is-sm"
                    onClick={() => setFileToDelete({ file, index })}
                  >
                    Delete
                  </button>
                </li>
              ))}
              {legacyFiles.map((url, index) => (
                <li key={`legacy-${index}`} className="opsui-list-item">
                  <div className="opsui-list-item-body">
                    <a className="opsui-link" href={url} target="_blank" rel="noreferrer">
                      📄 Uploaded file {index + 1}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <span className="opsui-muted" style={{ fontSize: 14 }}>None</span>
          )}
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------------ render
  return (
    <div className="opsui-root opsui-page">
      <div className="opsui-header">
        <div>
          <h1 className="opsui-title">Contractors</h1>
          <p className="opsui-subtitle">Sub contractors, their insurance coverage, licenses and compliance documents.</p>
        </div>
        <div className="opsui-header-actions">
          <button type="button" className="opsui-btn is-primary" onClick={() => setShowAddContractorPopup(true)}>
            + Add contractor
          </button>
        </div>
      </div>

      <div className="opsui-stats">
        <div className="opsui-stat">
          <div className="opsui-stat-label">Total contractors</div>
          <div className="opsui-stat-value">{stats.total}</div>
        </div>
        <div className="opsui-stat is-success">
          <div className="opsui-stat-label">Active</div>
          <div className="opsui-stat-value">{stats.active}</div>
        </div>
        <div className={`opsui-stat ${stats.soon ? "is-warn" : ""}`}>
          <div className="opsui-stat-label">Expiring within 30 days</div>
          <div className="opsui-stat-value">{stats.soon}</div>
        </div>
        <div className={`opsui-stat ${stats.expired ? "is-danger" : ""}`}>
          <div className="opsui-stat-label">Expired</div>
          <div className="opsui-stat-value">{stats.expired}</div>
        </div>
      </div>

      <div className="opsui-card">
        <div className="opsui-toolbar">
          <input
            className="opsui-input"
            type="search"
            placeholder="Search name, trade, skill, license #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="opsui-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive / Terminated</option>
          </select>
          <select className="opsui-select" value={complianceFilter} onChange={(e) => setComplianceFilter(e.target.value)}>
            <option value="all">All compliance</option>
            <option value="attention">Needs attention</option>
            <option value="expired">Expired only</option>
          </select>
          <span className="opsui-toolbar-spacer" />
          <span className="opsui-count">
            {sortedContractors.length} of {contractors.length}
          </span>
        </div>

        {loading ? (
          <EmptyState title="Loading contractors…" />
        ) : sortedContractors.length === 0 ? (
          contractors.length === 0 ? (
            <EmptyState
              title="No contractors yet"
              action={
                <button type="button" className="opsui-btn is-primary" onClick={() => setShowAddContractorPopup(true)}>
                  + Add your first contractor
                </button>
              }
            >
              Track sub contractors’ insurance and license expirations in one place.
            </EmptyState>
          ) : (
            <EmptyState title="No contractors match your filters" />
          )
        ) : (
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <thead>
                <tr>
                  <SortHeader label="Name / Company" sortKey="name" sort={sortConfig} onSort={handleSort} />
                  <SortHeader label="Skills" sortKey="skills" sort={sortConfig} onSort={handleSort} className="opsui-hide-sm" />
                  <SortHeader label="Insurance" sortKey="insurance" sort={sortConfig} onSort={handleSort} className="opsui-hide-sm" />
                  <SortHeader label="License Exp." sortKey="license" sort={sortConfig} onSort={handleSort} className="opsui-hide-sm" />
                  <SortHeader label="Compliance" sortKey="compliance" sort={sortConfig} onSort={handleSort} />
                  <SortHeader label="Status" sortKey="status" sort={sortConfig} onSort={handleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {sortedContractors.map((contractor) => {
                  const s = complianceSummary(contractor);
                  const inactive = (contractor.status || "Active") !== "Active";
                  return (
                    <tr
                      key={contractor.id}
                      className={`is-clickable${inactive ? " is-muted" : ""}`}
                      onClick={() => openDetails(contractor)}
                    >
                      <td>
                        <div className="opsui-cell-main">
                          {displayName(contractor)}{" "}
                          {contractor.staffId && (
                            <Pill tone="info" plain>
                              Staff
                            </Pill>
                          )}
                        </div>
                        <div className="opsui-cell-sub">
                          {[contractor.companyName && contractor.contactName, contractor.industry]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </td>
                      <td className="opsui-hide-sm" style={{ maxWidth: 240 }}>
                        <span className="opsui-cell-sub" style={{ fontSize: 13 }}>{formatSkills(contractor)}</span>
                      </td>
                      <td className="opsui-hide-sm">
                        <Pill tone={contractor.insuranceCertificates === "Yes" ? "success" : "neutral"} plain>
                          {contractor.insuranceCertificates || "No"}
                        </Pill>
                      </td>
                      <td className="opsui-hide-sm">
                        {contractor.contractorLicenseExpiration ? formatDate(contractor.contractorLicenseExpiration) : <span className="opsui-muted">—</span>}
                      </td>
                      <td>
                        {s ? (
                          <Pill tone={s.tone} title={`${s.policy} — ${formatDate(s.value)}`}>
                            {s.tone === "success" ? "All current" : `${s.policy}: ${s.label}`}
                          </Pill>
                        ) : (
                          <span className="opsui-muted">No dates</span>
                        )}
                      </td>
                      <td>
                        <StatusPill status={contractor.status || "Active"} />
                      </td>
                      <td className="opsui-cell-actions">
                        <button
                          type="button"
                          className="opsui-btn is-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetails(contractor);
                          }}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReminderRecipientsCard
        moduleKey="contractors"
        title="Insurance & license reminder emails"
        description="Who is emailed when a contractor’s insurance or license is about to expire or has expired."
        schedule="Reminders go out 30 days before and 30 days after each expiration date. Terminated contractors are skipped."
      />

      {/* ---------- add ---------- */}
      {showAddContractorPopup && (
        <OpsModal
          title="Add contractor"
          onClose={closeAdd}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={closeAdd} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveNewContractor} disabled={saving}>
                {saving ? "Saving…" : "Save contractor"}
              </button>
            </>
          }
        >
          <form onSubmit={saveNewContractor}>{renderForm(newContractor, onNewChange, true)}</form>
        </OpsModal>
      )}

      {/* ---------- details / edit ---------- */}
      {selectedContractor && editedContractor && (
        <OpsModal
          title={isEditing ? `Edit ${displayName(selectedContractor)}` : displayName(selectedContractor)}
          subtitle={isEditing ? undefined : "Contractor details"}
          onClose={closeDetails}
          footer={
            isEditing ? (
              <>
                <button
                  type="button"
                  className="opsui-btn"
                  onClick={() => {
                    setEditedContractor({ ...selectedContractor });
                    setEditFiles([]);
                    setIsEditing(false);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="button" className="opsui-btn is-primary" onClick={saveEditedContractor} disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </>
            ) : (
              <>
                <div className="opsui-foot-left">
                  <button type="button" className="opsui-btn is-danger" onClick={() => setShowDeleteConfirmation(true)}>
                    Delete
                  </button>
                </div>
                <button type="button" className="opsui-btn" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
                <button type="button" className="opsui-btn is-primary" onClick={closeDetails}>
                  Close
                </button>
              </>
            )
          }
        >
          {isEditing ? (
            <form onSubmit={saveEditedContractor}>{renderForm(editedContractor, onEditChange, false)}</form>
          ) : (
            renderDetails(selectedContractor)
          )}
        </OpsModal>
      )}

      {fileToDelete && (
        <ConfirmDialog
          title="Delete file?"
          message={<>Delete <strong>{fileToDelete.file?.name || `File ${fileToDelete.index + 1}`}</strong>? This removes it from storage.</>}
          onConfirm={handleDeleteComplianceFile}
          onCancel={() => setFileToDelete(null)}
          busy={deleting}
        />
      )}

      {showDeleteConfirmation && selectedContractor && (
        <ConfirmDialog
          title="Delete contractor?"
          message={<><strong>{displayName(selectedContractor)}</strong> will be permanently removed.</>}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          busy={deleting}
        />
      )}
    </div>
  );
};

export default ContractorsPage;
